import { useMemo } from 'react'
import { useAuthStore } from '@/stores'
import { CORNERSTONE_ORG_ID } from '@/lib/constants'

/** All available page slugs in the app. One slug per sidebar destination so
 *  each page can be granted or revoked individually. */
export const PAGE_SLUGS = [
  'dashboard',
  // CMMS
  'work_orders',
  'wo_archive',
  'scheduling',
  'sub_calendar',
  'crew_calendar',
  'crew_week',
  'crew_dashboard',
  'timesheets',
  'service_intake',
  'pm_schedules',
  'vendors',
  'rfqs',
  'dispatch',
  // Main
  'inspections',
  'estimates',
  'invoices',
  'finance',
  'receipts',
  'service_tickets',
  // Planning
  'fm_budget',
  'capital_plan',
  'facility_health',
  // Management
  'clients',
  'equipment',
  'parts_inventory',
  'forms',
  'goals',
  'knowledge_base',
  'reports',
  'scorecard',
  'team_reports',
  'messaging',
  'receipts_inbox',
  'reorder_queue',
  'deliveries',
  'power_tools',
  'gate_board',
  'program_report',
  'closeout_review',
  'employee_reviews',
  // Sales
  'prospect_sites',
  // Tools
  'ask_opsyri',
  '360_tours',
  'takeoffs',
  'recordings',
  // Admin & settings
  'team_management',
  'roles',
  'workflow',
  'settings',
  'billing',
] as const

export type PageSlug = (typeof PAGE_SLUGS)[number]

/* ------------------------------------------------------------------------- *
 * Permission encoding
 *
 * `users.page_permissions` is a flat string array. Three entry forms:
 *   'work_orders'   grant  — subject to the page's role tier (see PAGE_TIER)
 *   '+reports'      force  — grant even if the role tier would normally block it
 *   '-invoices'     deny   — revoke, even from an admin or a '*' grant
 *   '*'             all    — every page the role tier allows
 *
 * That combination is what makes "pick specific pages outside their role"
 * work in both directions: force-grant an admin-only page to one member, or
 * deny a single page to an admin who otherwise sees everything.
 * ------------------------------------------------------------------------- */
export const FORCE_PREFIX = '+'
export const DENY_PREFIX = '-'

export const grantEntry = (page: PageSlug) => page as string
export const forceEntry = (page: PageSlug) => `${FORCE_PREFIX}${page}`
export const denyEntry = (page: PageSlug) => `${DENY_PREFIX}${page}`

/** Role tier a page normally requires. An explicit force-grant ('+slug')
 *  overrides this for one user; the tier still applies to everyone else. */
export const PAGE_TIER: Partial<Record<PageSlug, 'admin' | 'platform_admin'>> = {
  // Under development / internal testing — platform admins only by default
  service_tickets: 'platform_admin',
  pm_schedules: 'platform_admin',
  fm_budget: 'platform_admin',
  capital_plan: 'platform_admin',
  facility_health: 'platform_admin',
  equipment: 'platform_admin',
  goals: 'platform_admin',
  knowledge_base: 'platform_admin',
  reports: 'platform_admin',
  messaging: 'platform_admin',
  recordings: 'platform_admin',
  rfqs: 'platform_admin',
  // Org-admin pages (payroll, financial inboxes, dispatch, settings)
  crew_dashboard: 'admin',
  timesheets: 'admin',
  receipts_inbox: 'admin',
  receipts: 'admin',
  reorder_queue: 'admin',
  deliveries: 'admin',
  program_report: 'admin',
  closeout_review: 'admin',
  employee_reviews: 'admin',
  dispatch: 'admin',
  scorecard: 'admin',
  team_reports: 'admin',
  roles: 'admin',
  workflow: 'admin',
  billing: 'admin',
}

/** Sub-pages fall back to their parent page when they aren't mentioned
 *  explicitly. Keeps every permission list written before these slugs existed
 *  working (a member with `work_orders` still gets Scheduling) while still
 *  letting an admin grant or deny the sub-page on its own. */
export const PAGE_INHERITS: Partial<Record<PageSlug, PageSlug>> = {
  wo_archive: 'work_orders',
  scheduling: 'work_orders',
  sub_calendar: 'work_orders',
  crew_calendar: 'work_orders',
  crew_week: 'work_orders',
  crew_dashboard: 'work_orders',
  timesheets: 'work_orders',
  receipts_inbox: 'work_orders',
  receipts: 'finance',
  reorder_queue: 'work_orders',
  deliveries: 'work_orders',
  gate_board: 'work_orders',
  program_report: 'work_orders',
  closeout_review: 'work_orders',
  rfqs: 'vendors',
  takeoffs: 'estimates',
  scorecard: 'reports',
  team_reports: 'reports',
  roles: 'team_management',
  workflow: 'team_management',
  billing: 'settings',
}

/** Pages that only ever work for admins. The tables behind them require
 *  org-admin RLS, so a per-user grant would just be a dead page — these stay
 *  tied to the role and are not force-grantable. */
export const ADMIN_LOCKED_PAGES: PageSlug[] = ['team_management', 'roles', 'workflow', 'billing']

/** Pages every internal user of an enabled org gets without a grant. They can
 *  still be revoked individually with a deny entry. */
/** Pages every internal user of an enabled org gets without a grant. They can
 *  still be revoked individually with a deny entry.
 *
 *  `finance` is baseline on purpose: its real gate is the per-user
 *  `can_view_financials` toggle in `isPageAvailable`, so an admin flips ONE
 *  switch on the Team page rather than a toggle plus a page grant. Turning the
 *  toggle off hides the page again; '-finance' still revokes it explicitly. */
export const BASELINE_PAGES: PageSlug[] = ['parts_inventory', 'ask_opsyri', 'finance', 'power_tools']

/** Features still scoped to Cornerstone. Tenant scoping is hard — a per-user
 *  grant cannot open these for another org. */
export const CORNERSTONE_ONLY_PAGES: PageSlug[] = [
  'forms', '360_tours', 'prospect_sites', 'service_intake', 'dispatch', 'power_tools',
  'employee_reviews',
]

/** Pages that are internal-staff only (never portal users). */
export const INTERNAL_ONLY_PAGES: PageSlug[] = ['parts_inventory', 'ask_opsyri', 'power_tools', 'employee_reviews']

/** Back-compat: pages whose default tier is platform admin. */
export const PLATFORM_ADMIN_ONLY_PAGES: PageSlug[] = (PAGE_SLUGS as readonly PageSlug[]).filter(
  (p) => PAGE_TIER[p] === 'platform_admin'
)

/** Default page permissions for new members (admins always get all) */
export const MEMBER_DEFAULT_PAGES: PageSlug[] = [
  'dashboard', 'work_orders', 'scheduling', 'crew_week', 'pm_schedules', 'inspections',
  'service_tickets', 'equipment', '360_tours', 'reports', 'messaging', 'forms',
]

/** Columns that can be hidden per page */
export const HIDEABLE_COLUMNS: Record<string, { key: string; label: string }[]> = {
  work_orders: [
    { key: 'total_cost', label: 'Total Cost' },
    { key: 'estimated_cost', label: 'Estimated Cost' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'labor_cost', label: 'Labor Cost' },
    { key: 'parts_cost', label: 'Parts Cost' },
  ],
}

/** Human-readable labels for every page slug. Single source of truth shared by
 *  the Team permissions editor and the Help Center guides. */
export const PAGE_LABELS: Record<PageSlug, string> = {
  dashboard: 'Dashboard',
  work_orders: 'Work Orders',
  wo_archive: 'Archived Work Orders',
  scheduling: 'Scheduling',
  sub_calendar: 'Sub Calendar',
  crew_calendar: 'Crew Calendar',
  crew_week: 'My Crew Week',
  crew_dashboard: 'Crew Dashboard',
  timesheets: 'Timesheets',
  service_intake: 'Service Intake',
  pm_schedules: 'PM Schedules',
  vendors: 'Vendors',
  rfqs: 'RFQs',
  dispatch: 'ISP Dispatch',
  inspections: 'Inspections',
  estimates: 'Estimates',
  invoices: 'Invoices',
  finance: 'Billing / Revenue',
  receipts: 'Receipts',
  service_tickets: 'Service Tickets',
  fm_budget: 'FM Budget',
  capital_plan: 'Capital Plan',
  facility_health: 'Facility Health',
  clients: 'Clients',
  equipment: 'Equipment',
  parts_inventory: 'Parts Inventory',
  forms: 'Forms',
  goals: 'Goals',
  knowledge_base: 'Knowledge Base',
  reports: 'Reports',
  scorecard: 'Leadership Scorecard',
  team_reports: 'Team Reports',
  messaging: 'Messaging',
  receipts_inbox: 'Receipt Inbox',
  reorder_queue: 'Reorder Queue',
  deliveries: 'Deliveries',
  power_tools: 'Power Tools',
  gate_board: 'Gate Board',
  program_report: 'Program Report',
  closeout_review: 'Close-out Review',
  employee_reviews: 'Employee Reviews',
  prospect_sites: 'Prospect Sites',
  ask_opsyri: 'Ask Opsyri',
  '360_tours': '360° Tours',
  takeoffs: 'Blueprint Takeoff',
  recordings: 'Screen Recorder',
  team_management: 'Team Management',
  roles: 'Roles & Permissions',
  workflow: 'Workflow Settings',
  settings: 'Settings',
  billing: 'Billing',
}

/** Page slugs grouped to mirror the sidebar sections. Drives the grouped
 *  layout of the per-member Page Access editor. */
export const PAGE_GROUPS: { label: string; pages: PageSlug[] }[] = [
  { label: 'Main', pages: ['dashboard', 'inspections', 'estimates', 'invoices', 'finance', 'receipts', 'timesheets', 'service_tickets'] },
  {
    label: 'CMMS',
    pages: [
      'work_orders', 'wo_archive', 'scheduling', 'sub_calendar', 'crew_calendar',
      'crew_week', 'crew_dashboard', 'service_intake', 'pm_schedules', 'vendors',
      'rfqs', 'dispatch',
    ],
  },
  { label: 'Planning', pages: ['fm_budget', 'capital_plan', 'facility_health'] },
  {
    label: 'Management',
    pages: [
      'clients', 'equipment', 'forms', 'goals', 'knowledge_base', 'reports',
      'scorecard', 'team_reports', 'messaging', 'receipts_inbox', 'reorder_queue',
      'deliveries', 'power_tools', 'gate_board', 'program_report', 'closeout_review',
      'employee_reviews',
    ],
  },
  { label: 'Sales / Prospecting', pages: ['prospect_sites'] },
  { label: 'Tools', pages: ['ask_opsyri', '360_tours', 'takeoffs', 'recordings'] },
  { label: 'Admin & Settings', pages: ['team_management', 'roles', 'workflow', 'settings', 'billing'] },
]

/** One-click access templates. `pages: ['*']` grants everything. These only set
 *  page visibility — column and parts-edit access are configured separately. */
export const PAGE_PRESETS: { label: string; description: string; pages: string[] }[] = [
  {
    label: 'Full access',
    description: 'Every page in the sidebar.',
    pages: ['*'],
  },
  {
    label: 'Office / manager',
    description: 'Day-to-day operations, planning and reporting — no admin settings.',
    pages: [
      'dashboard', 'work_orders', 'wo_archive', 'scheduling', 'sub_calendar', 'crew_calendar',
      'pm_schedules', 'vendors', 'inspections', 'estimates', 'takeoffs', 'invoices',
      'clients', 'equipment', 'reports', 'forms', 'messaging', 'goals', 'ask_opsyri',
    ],
  },
  {
    label: 'Field tech',
    description: 'What a technician needs in the field.',
    pages: [...MEMBER_DEFAULT_PAGES],
  },
  {
    label: 'Read-only basics',
    description: 'Dashboard, work orders and reports only.',
    pages: ['dashboard', 'work_orders', 'reports'],
  },
]

/** Column permissions shape: { "work_orders": ["total_cost", "vendor"] } = hidden columns */
export type ColumnPermissions = Record<string, string[]>

/** Everything the access check needs about the user being evaluated. Kept
 *  separate from the hook so the Team editor can preview another member's
 *  effective access with the exact same logic the app enforces. */
export interface PageAccessContext {
  isAdmin: boolean
  isPlatformAdmin: boolean
  isInternal: boolean
  isCornerstoneOrg: boolean
  canAccessInspections: boolean
  /** `users.can_view_financials`, or admin. Hard gate on the Finance page and
   *  every dollar figure it shows. Mirrors the server's can_view_financials(). */
  canViewFinancials: boolean
  /** `users.can_review_receipts`. Opens the Receipts page without finance access. */
  canReviewReceipts?: boolean
  /** `users.can_manage_payroll`, or admin. Opens Timesheets and lets them
   *  enter pay adjustments. Mirrors the server's can_manage_payroll(). */
  canManagePayroll?: boolean
}

/** Tenant / feature scoping. Hard gates — a per-user grant cannot open these. */
function isPageAvailable(ctx: PageAccessContext, page: PageSlug): boolean {
  // Internal-only pages are also Cornerstone-only for now (matches the
  // previous `isInternal && isCornerstoneOrg` gate on parts + Ask Opsyri).
  if (INTERNAL_ONLY_PAGES.includes(page) && !(ctx.isInternal && ctx.isCornerstoneOrg)) return false
  if (CORNERSTONE_ONLY_PAGES.includes(page) && !ctx.isCornerstoneOrg) return false
  if (page === 'inspections' && !ctx.canAccessInspections) return false
  // Finance: internal staff with the financial-access toggle only. Portal users
  // never qualify, matching the server-side can_view_financials().
  if (page === 'finance' && !(ctx.isInternal && ctx.canViewFinancials)) return false
  // Receipts: finance, or the named receipt reviewer.
  if (page === 'receipts' && !(ctx.isInternal && (ctx.canViewFinancials || ctx.canReviewReceipts))) return false
  return true
}

/**
 * Resolve whether a user can open a page.
 *
 * Precedence (highest first):
 *   1. tenant / feature availability      — hard, never overridable per user
 *   2. explicit deny  '-slug'             — revokes from anyone, admins included
 *   3. explicit force '+slug'             — grants past the page's role tier
 *   4. role tier (PAGE_TIER)              — admin / platform-admin defaults
 *   5. admin role, or '*', or plain grant
 *   6. parent page (PAGE_INHERITS) for sub-pages
 */
export function canAccessPage(
  ctx: PageAccessContext,
  permissions: string[],
  page: PageSlug
): boolean {
  if (!isPageAvailable(ctx, page)) return false

  const hasAll = permissions.includes('*')

  const visit = (p: PageSlug, depth: number): boolean => {
    if (permissions.includes(denyEntry(p))) return false
    // Payroll staff always get Timesheets, whatever their role tier.
    if (p === 'timesheets' && ctx.canManagePayroll) return true
    // Admin-locked pages ignore force grants — the backend would reject them.
    if (ADMIN_LOCKED_PAGES.includes(p) && !ctx.isAdmin) return false
    if (permissions.includes(forceEntry(p))) return true

    const tier = PAGE_TIER[p]
    if (tier === 'platform_admin' && !ctx.isPlatformAdmin) return false
    if (tier === 'admin' && !ctx.isAdmin) return false

    if (permissions.includes(p)) return true
    if (ctx.isAdmin || hasAll) return true
    if (BASELINE_PAGES.includes(p)) return true

    const parent = PAGE_INHERITS[p]
    if (parent && depth < 4) return visit(parent, depth + 1)
    return false
  }

  return visit(page, 0)
}

/**
 * Toggle one page in a permission array, returning the new array.
 * Turning a page ON force-grants it when the user's role tier would otherwise
 * block it; turning it OFF adds a deny entry only when the page would still
 * resolve to allowed (admin role, '*', a parent grant, or baseline).
 */
export function togglePagePermission(
  ctx: PageAccessContext,
  permissions: string[],
  page: PageSlug,
  turnOn: boolean
): string[] {
  const stripped = permissions.filter(
    (p) => p !== page && p !== forceEntry(page) && p !== denyEntry(page)
  )

  if (turnOn) {
    const tier = PAGE_TIER[page]
    const tierMet =
      !tier || (tier === 'admin' ? ctx.isAdmin : ctx.isPlatformAdmin)
    // Already allowed without an entry (admin, '*', parent grant, baseline)?
    // Leave the list clean rather than adding a redundant grant.
    if (tierMet && canAccessPage(ctx, stripped, page)) return stripped
    return [...stripped, tierMet ? grantEntry(page) : forceEntry(page)]
  }

  // Still allowed after removing the grant → needs an explicit deny.
  if (canAccessPage(ctx, stripped, page)) return [...stripped, denyEntry(page)]
  return stripped
}

export function usePermissions() {
  const { user, organization } = useAuthStore()

  return useMemo(() => {
    const role = user?.role ?? 'member'
    const portalType = user?.portal_type ?? 'internal'
    const permissions = user?.page_permissions ?? ['*']
    const columnPerms: ColumnPermissions = (user as any)?.column_permissions ?? {}
    // Super admin: org-scoped tier above admin (flag, add-on to the normal role).
    // Only super admins can edit other admins' permissions and see staff rates.
    const isSuperAdmin = !!(user as any)?.is_super_admin
    // account_admin + super admins are full admins in the UI too (matches backend is_org_admin).
    const isAdmin = role === 'admin' || role === 'account_admin' || isSuperAdmin

    /** Inspections are Cornerstone-only (or invited clients) until feature is opened up */
    const isCornerstoneOrg = user?.organization_id === CORNERSTONE_ORG_ID
    const isInvitedClient = portalType === 'client' && permissions.includes('inspections')
    const canAccessInspections = isCornerstoneOrg || isInvitedClient

    const isPlatformAdmin = !!(user as any)?.is_platform_admin
    const isInternal = portalType === 'internal'
    // Parts inventory: view is open to all internal staff (edit is gated
    // separately by canManageParts). Not tied to a per-user page permission.
    const canManageParts = isAdmin || !!(user as any)?.can_manage_parts
    // Financial access: the separate admin-controlled toggle. Admins always
    // qualify. Mirrors the server-side can_view_financials() exactly — the UI
    // gate is convenience; the RPCs enforce it for real.
    const canViewFinancials = isAdmin || !!(user as any)?.can_view_financials
    // Receipt reviewer: the named person (users.can_review_receipts) or any
    // admin. Mirrors the server-side can_review_receipts(). The flag alone
    // still decides who gets the "receipt to review" notifications.
    const canReviewReceipts = isInternal && (isAdmin || !!(user as any)?.can_review_receipts)
    // Payroll staff: the named people (users.can_manage_payroll) or any admin.
    // Mirrors the server-side can_manage_payroll().
    const canManagePayroll = isInternal && (isAdmin || !!(user as any)?.can_manage_payroll)

    const ctx: PageAccessContext = {
      isAdmin,
      isPlatformAdmin,
      isInternal,
      isCornerstoneOrg,
      canAccessInspections,
      canViewFinancials,
      canReviewReceipts,
      canManagePayroll,
    }

    const canAccess = (page: PageSlug): boolean => canAccessPage(ctx, permissions, page)

    /** Check if a column is visible on a given page. Admins see everything. */
    const canSeeColumn = (page: string, column: string): boolean => {
      if (isAdmin) return true
      const hidden = columnPerms[page]
      if (!hidden || hidden.length === 0) return true
      return !hidden.includes(column)
    }

    return {
      role,
      portalType,
      isAdmin,
      isSuperAdmin,
      isPlatformAdmin,
      canManageParts,
      canViewFinancials,
      canReviewReceipts,
      canManagePayroll,
      isVendor: portalType === 'vendor',
      isClient: portalType === 'client',
      isInternal,
      accessContext: ctx,
      canAccess,
      canSeeColumn,
      canDelete: isAdmin,
      canManageTeam: isAdmin,
      canManageBilling: isAdmin,
      canManageBranding: isAdmin,
      canAccessInspections,
      isCornerstoneOrg,
      permissions,
      columnPermissions: columnPerms,
      organizationId: user?.organization_id ?? null,
      userId: user?.id ?? null,
    }
  }, [user, organization])
}
